import { useEffect, useState } from 'react';
import {
  PIPED_MEDIA_MAX_ATTEMPTS,
  PIPED_MEDIA_RETRY_MS,
  PIPED_MEDIA_RID_PARAM,
  pipedMediaPath,
} from '../../constants/eyetrackingConstants';
import type { PipedMediaState, PipedPayload } from './pipedMedia';

const API_URL = import.meta.env.VITE_API_URL || '';

export const readRid = (): string | null =>
  new URLSearchParams(window.location.search).get(PIPED_MEDIA_RID_PARAM);

/**
 * Fetch the respondent's piped content if this survey has piped questions. Bump `version` to
 * refetch (an embedded study finished, so its slots now exist); piped questions wait for it
 * rather than be skipped on the older payload.
 */
export function usePipedMedia(
  enabled: boolean,
  surveyId: number | undefined,
  version = 0,
): PipedMediaState {
  const [state, setState] = useState<PipedMediaState>({ status: 'idle' });

  useEffect(() => {
    if (!enabled || surveyId === undefined) return;
    const rid = readRid();
    if (!rid) {
      setState({ status: 'error' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });

    const attempt = async (n: number): Promise<void> => {
      try {
        const res = await fetch(`${API_URL}${pipedMediaPath(surveyId, rid)}`);
        if (!res.ok) throw new Error(`piped media ${res.status}`);
        const data: PipedPayload = await res.json();
        if (!cancelled) setState({ status: 'ready', data });
      } catch {
        if (cancelled) return;
        if (n + 1 >= PIPED_MEDIA_MAX_ATTEMPTS) {
          setState({ status: 'error' });
          return;
        }
        setTimeout(() => void attempt(n + 1), PIPED_MEDIA_RETRY_MS);
      }
    };
    void attempt(0);
    return () => {
      cancelled = true;
    };
  }, [enabled, surveyId, version]);

  return state;
}
